//Motif Imports
import {
  MotifModal,
  MotifModalHeader,
  MotifButton,
  MotifCard,
  MotifCardBody,
  MotifCardHeader,
  MotifTooltip,
  MotifModalFooter,
} from '@ey-xd/motif-react';
import { MotifIconButton } from '@ey-xd/motif-react/components/button';

//  Generic imports
import React, { useRef, useState } from "react";

//  Internal imports
import "./ImprovePromptModal.css";
import ExploreService from "../../services/explore.service";
import { useEffect } from 'react';
import regenerate from "../../assets/images/byod-images/re-generate.svg";
import loadingCircle from "../../assets/images/byod-images/Loading_indicator_circlesTable.svg";


export function ImprovePromptModal({
    onClose,
    OpenModal,
    requestData,
    onUsePrompt
}) {
    const [open, setOpen] = useState(true);
    const exploreService = new ExploreService();
    const [isLoading, setIsLoading] = useState(false);
    const [suggestedPrompt, setSuggestedPrompt] = useState('');
    const originalRef = useRef(null);
    const [editedPrompt, setEditedPrompt] = useState('');
    const [isEdited, setIsEdited] = useState(false);
    const [isOriginalFocused, setIsOriginalFocused] = useState(false);

    const onCloseClick = () => {
        onClose(false);
    }

    useEffect(() => {
        if (open) {
            const originalText = originalRef.current?.innerText.trim();
            setEditedPrompt(originalText);
            setIsEdited(false);
        }
    }, [open]);

    const handleInput = (e) => {
        const currentText = e.currentTarget.innerText.trim();
        setEditedPrompt(currentText);
        const originalText = sanitizeHTML(requestData.questionBar).trim();
        setIsEdited(currentText !== originalText);
    };

    function sanitizeHTML(input) {
        const doc = new DOMParser().parseFromString(input, 'text/html');
        return doc.body.textContent || ""; // Extracts just the text without HTML
    }
    useEffect(() => {
        if (open) {
            generate();
        }
    }, [open]);

    const generate = async () => {
        setIsLoading(true);
        let prompt = '';
        if (isEdited) {
            prompt = editedPrompt;
        }
        else {
            prompt = sanitizeHTML(requestData.questionBar);
        }
    
        const response = await exploreService.GetPromptResponse(
            requestData.conv_UUID,
            requestData.project_UUID,
            prompt,
            "All",
            requestData.emailId,
            requestData.confPublic,
            requestData.fileList,
            "N",
            "Y"

            );
        if (response) {
            setIsLoading(false);
            if (response && response.responseContent) {
                setSuggestedPrompt(response.responseContent);
            }
        }
    }

    // Function to format markdown text to display with line breaks and bullets
    const formatMarkdownText = (text) => {
        if (!text) return '';
        
        // Remove bold markdown characters (**)
        let formattedText = text.replace(/\*\*(.*?)\*\*/g, '$1');
        
        // Remove italic markdown characters (* or _)
        formattedText = formattedText.replace(/(\*|_)(.*?)(\*|_)/g, '$2');
        
        // Replace all line breaks with <br> tags
        const withLineBreaks = formattedText.replace(/\n/g, '<br>');
        
        // Format bullet points (- or *) with proper spacing
        const withBullets = withLineBreaks.replace(/<br>(\s*[-*]\s+)/g, '<br>$1');
        
        // Format numbered lists (1., 2., etc.)
        const withNumbers = withBullets.replace(/<br>(\s*\d+\.\s+)/g, '<br>$1');
        
        return withNumbers;
    };    // Function to format the prompt for the rich text editor
    const formatPromptForRichEditor = (text) => {
        if (!text) return '';
        
        // If the text already has HTML tags, we assume it's already formatted
        if (text.match(/<[^>]*>/)) {
            return text;
        }
        
        // Remove markdown formatting like bold and italic markers
        // so the text appears as raw text without formatting
        const cleanText = text.trim();
        
        // Remove bold markdown characters (**)
        let rawText = cleanText.replace(/\*\*(.*?)\*\*/g, '$1');
        
        // Remove italic markdown characters (* or _)
        rawText = rawText.replace(/\*(.*?)\*/g, '$1');
        rawText = rawText.replace(/_(.*?)_/g, '$1');
        
        // Escape any HTML that might be in the text to prevent rendering issues
        const escapedText = rawText
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;");
        
        // Replace newlines with <br> to preserve line breaks
        const formattedText = escapedText.replace(/\n/g, '<br>');
        
        // Wrap in <p> tags to create a proper HTML fragment
        return `<p>${formattedText}</p>`;
    };

    return (
        <MotifModal id="improvePromptModal" show={open} size="xl" focusTrapOptions={{
            tabbableOptions: {
                displayCheck: 'none'
            }
        }}
            onClose={() => { onCloseClick() }}
            className="improve-prompt-modal"
        >
            <MotifModalHeader className="modal-header-container">
                Improve Prompt
            </MotifModalHeader>
            <div className="cards-container">
                <MotifCard className='custom-card'>
                    <MotifCardHeader className="prompt-card-header">
                        <div className="header-container">
                            <div className="header-left">
                                <span className="motif-h6">
                                    Original
                                </span>
                                <div className="header-right">
                                    <div className="header-button-placeholder"></div>
                                </div>
                            </div>
                        </div>
                    </MotifCardHeader>
                    <MotifCardBody className={`improve-prompt-modal-body original-body ${isEdited ? "edited" : ""} ${isOriginalFocused ? "focused" : ""}`}>
                        <p
                            ref={originalRef}
                            style={{
                                margin: 0
                            }}
                            contentEditable={true}
                            onInput={handleInput}
                            onFocus={() => setIsOriginalFocused(true)}
                            onBlur={() => setIsOriginalFocused(false)}
                        >
                            {sanitizeHTML(requestData.questionBar)}
                        </p>
                    </MotifCardBody>
                </MotifCard>
                <MotifCard className='custom-card'>
                    <MotifCardHeader className="prompt-card-header">
                        <div className="header-left">
                            <span className="motif-h6">
                                Suggested
                            </span>
                        </div>
                        <div className="header-right">
                            {!isEdited ? (

                                <MotifTooltip
                                    allowClick={false}
                                    allowHover={true}
                                    hideCloseButton={true}
                                    placement="top"
                                    contentClassName='improve-prompt-regTooltip'
                                    trigger={
                                        <MotifButton
                                            aria-label="Click here to see options"
                                            disabled={!isEdited}
                                            onClick={generate}
                                            className="regenerate-button"
                                        >
                                            <img
                                                src={regenerate}
                                                title=""
                                                alt="regenerate"
                                                className='rege-icon'
                                            />
                                            <span className='regene-text'>Regenerate</span>
                                        </MotifButton>
                                    }
                                >
                                    Modify the original<br /> prompt to Regenerate
                                </MotifTooltip>

                            ) : (
                                <MotifButton
                                    aria-label="Click here to see options"
                                    onClick={generate}
                                    className="regenerate-button"
                                >
                                    <img
                                        src={regenerate}
                                        title=""
                                        alt="regenerate"
                                        className='rege-icon'
                                    />
                                    <span className='regene-text'>Regenerate</span>
                                </MotifButton>
                            )}
                        </div>
                    </MotifCardHeader>
                    <MotifCardBody className={`improve-prompt-modal-body${isLoading ? " loading" : ""}`}>
                        {isLoading ? (
                            <div className="loading-wrapper">
                                <span className="add-document-file-status-prompt-modal">
                                    <img
                                        className="rotate-icon loader-text"
                                        src={loadingCircle}
                                        alt="Loading"
                                    />
                                    Generating Prompt
                                </span>
                            </div>
                        ) : (
                            <div 
                                className="raw-markdown-wrapper" 
                                style={{ margin: 0, whiteSpace: 'pre-wrap', lineHeight: 1.5 }}
                                dangerouslySetInnerHTML={{ __html: formatMarkdownText(suggestedPrompt) }}
                            />
                        )}
                    </MotifCardBody>
                </MotifCard>

            </div>
            <MotifModalFooter className='Improve-prompt-Footer'>
                <React.Fragment key=".0">                <MotifButton
                        label="btnUseprompt"
                        onClick={() => {
                            // Format the prompt to remove markdown formatting and display as raw text
                            const rawTextPrompt = formatPromptForRichEditor(suggestedPrompt);
                            onUsePrompt?.(rawTextPrompt);
                            onCloseClick(); // Close the modal after using the prompt
                        }}
                        size="medium"
                        type="button"
                        className='btn-use-prompt'
                        disabled={isLoading}
                    >
                        Use Prompt
                    </MotifButton>
                    <MotifButton
                        label="btnCancel"
                        onClick={() => { onCloseClick() }}
                        size="medium"
                        type="button"
                        variant="secondary"
                        className='btn-cancel'
                    >
                        Cancel
                    </MotifButton>
                </React.Fragment>
            </MotifModalFooter>
        </MotifModal>
    )
}